import React from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import Typography from '../Typography';
import LinkIcon from '../icons/LinkIcon';
import Dialog from '../Dialog';

function GridDialog(props) {
  const { className, classes, data, imageStyle, ...dialogProps } = props;
  const { image, title, url, subtitle, description, badge } = data;
  return (
    <Dialog {...dialogProps} className={classNames(classes.dialog, className)}>
      <div className={classes.imageWrapper}>
        {image && (
          <img
            src={image.url}
            alt={image.alt || title}
            className={classNames(
              classes.image,
              imageStyle === 'rectangular' && classes.imageRectangular,
              imageStyle === 'circle' && classes.imageCircle,
              imageStyle === 'rounded' && classes.imageRounded,
            )}
          />
        )}
        {badge && (
          <div
            className={classNames(classes.badge, imageStyle !== 'circle' && classes.badgeOffset)}
          >
            <img
              src={badge.url}
              alt={badge.alt || `${title} badge`}
              className={classes.badgeImage}
            />
            <div className={classes.badgeBorder} />
          </div>
        )}
      </div>
      <div className={classes.textWrapper}>
        {url && url !== 'http://' ? (
          <a href={url} className={classes.link} target="_blank" rel="noopener noreferrer">
            <Typography className={classes.title} type="title">
              {title}
            </Typography>
            <LinkIcon className={classes.linkIcon} />
          </a>
        ) : (
          <Typography className={classes.title} type="title">
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography className={classes.subtitle} type="body2" paragraph>
            {typeof subtitle === 'string' ? subtitle : subtitle.join('\n')}
          </Typography>
        )}
        <Typography>{description}</Typography>
      </div>
    </Dialog>
  );
}

GridDialog.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  imageStyle: PropTypes.oneOf(['square', 'circle', 'rounded']),
  data: PropTypes.shape({
    image: PropTypes.shape({
      url: PropTypes.string.isRequired,
    }),
    badge: PropTypes.shape({
      url: PropTypes.string.isRequired,
      alt: PropTypes.string,
    }),
    title: PropTypes.string,
    subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    description: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
};

GridDialog.defaultProps = {
  className: null,
  imageStyle: 'square',
  data: {
    image: null,
    badge: null,
    title: null,
    subtitle: null,
    description: null,
    url: null,
  },
};

const stylesheet = theme => ({
  dialog: {
    display: 'flex',
    alignItems: 'flex-start',
    [theme.breakpoints.down('md')]: {
      display: 'block',
    },
  },
  imageWrapper: {
    width: '100%',
    maxWidth: 260,
    margin: '0 auto',
    position: 'relative',
    [theme.breakpoints.down('md')]: {
      marginBottom: 5 * theme.spacing.unit,
    },
  },
  image: {
    width: '100%',
  },
  imageCircle: {
    borderRadius: '50%',
  },
  imageRounded: {
    borderRadius: 16,
  },
  badge: {
    width: 64,
    height: 64,
    position: 'absolute',
    bottom: 0,
    right: 0,
    borderRadius: '50%',
    overflow: 'hidden',
  },
  badgeOffset: {
    right: '-5%',
    bottom: '-5%',
  },
  badgeImage: {
    width: '100%',
    height: '100%',
    display: 'block',
  },
  badgeBorder: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    top: 0,
    left: 0,
    position: 'absolute',
    border: '2px solid rgba(125, 125, 125, 0.333)',
  },
  textWrapper: {
    marginLeft: 8 * theme.spacing.unit,
    flex: 1,
    [theme.breakpoints.down('md')]: {
      width: '100%',
      marginLeft: 0,
    },
  },
  link: {
    color: 'inherit',
    fill: 'inherit',
    textDecoration: 'none',
    transition: `opacity ${theme.transitions.duration.shortest}ms`,
    willChange: 'opacity',
    '&:hover': {
      opacity: 0.7,
    },
  },
  title: {
    marginBottom: theme.spacing.medium,
    display: 'inline-block',
  },
  linkIcon: {
    fill: 'inherit',
    opacity: 'inherit',
    marginBottom: -4,
    marginLeft: theme.spacing.unit,
  },
  subtitle: {
    marginTop: theme.spacing.unit,
  },
});

export default injectSheet(stylesheet)(GridDialog);



// WEBPACK FOOTER //
// ./src/site-render/src/GridSection/GridDialog.js